import type {
  ConfigurationModule,
  FormBuilderTemplateFetchResult,
  FormBuilderValues,
  TemplateDetails,
} from '@ingenico/react-form-builder';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { firstValueFrom, isObservable } from 'rxjs';
import { getDataFileResourceIds } from './ParameterModuleTab.utils';

export function useParameterModuleTemplateResources({
  configModule,
  initialTemplate,
  getCurrentValues,
  fetchParameterTemplate,
}: {
  readonly configModule?: ConfigurationModule | null;
  readonly initialTemplate?: TemplateDetails;
  readonly getCurrentValues: () => FormBuilderValues | undefined;
  readonly fetchParameterTemplate?: (id: string) => FormBuilderTemplateFetchResult;
}) {
  const [activeTemplate, setActiveTemplate] = useState({ id: '' });
  const setActiveTemplateId = useCallback((id: string) => {
    setActiveTemplate((previous) => (previous.id === id ? previous : { id }));
  }, []);
  const onFormBuilderInitialized = useCallback(() => {
    // A refetch can replace the form values without changing the active template ID.
    setActiveTemplate((previous) => ({ ...previous }));
  }, []);
  const [loadedTemplates, setLoadedTemplates] = useState<Record<string, TemplateDetails>>({});
  const rememberTemplate = useCallback((template: TemplateDetails) => {
    setLoadedTemplates((previous) =>
      previous[template.id] === template ? previous : { ...previous, [template.id]: template },
    );
  }, []);

  useEffect(() => {
    if (initialTemplate) rememberTemplate(initialTemplate);
  }, [initialTemplate, rememberTemplate]);

  const templateToDisplay = loadedTemplates[activeTemplate.id] ?? initialTemplate;
  const dataFileResourceIds = useMemo(() => {
    const currentValues = getCurrentValues();
    return getDataFileResourceIds(
      currentValues?.parameterSetValues.length ? { parameterSets: currentValues.parameterSetValues } : configModule,
      loadedTemplates[activeTemplate.id] ?? initialTemplate,
    );
  }, [configModule, loadedTemplates, activeTemplate, initialTemplate, getCurrentValues]);

  const getCurrentDataFileResourceIds = () => {
    const currentValues = getCurrentValues();
    if (!currentValues) return dataFileResourceIds;
    const templates = { ...loadedTemplates };
    if (templateToDisplay) templates[templateToDisplay.id] = templateToDisplay;
    return [
      ...new Set(
        Object.values(templates).flatMap((template) =>
          getDataFileResourceIds({ parameterSets: currentValues.parameterSetValues }, template),
        ),
      ),
    ].sort();
  };

  const loadTemplate = useCallback(
    async (id: string) => {
      if (!fetchParameterTemplate) throw new Error('No parameter template resolver configured');
      const result = fetchParameterTemplate(id);
      const template = await (isObservable(result) ? firstValueFrom(result) : result);
      rememberTemplate(template);
      return template;
    },
    [fetchParameterTemplate, rememberTemplate],
  );

  return {
    templateToDisplay,
    dataFileResourceIds,
    getCurrentDataFileResourceIds,
    setActiveTemplateId,
    loadTemplate,
    onFormBuilderInitialized,
  };
}
